/**
 * Psychological Flexibility Diagnostic Page
 *
 * Standalone wellbeing assessment measuring psychological inflexibility patterns.
 * No transformation value required - focuses purely on wellbeing outcomes.
 *
 * Features:
 * - Step 1: Introduction (info about diagnostic)
 * - Step 2: Pattern Assessment (10 flexibility pattern sliders)
 * - No persona selection, no value estimation
 * - Based on ACT (Acceptance and Commitment Therapy) framework
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { calculatePsychologicalFlexibilityV2 } from '../../utils/algorithms';
import DiagnosticHeader from '../../components/diagnostics/DiagnosticHeader';
import '../../styles/diagnostics/index.css';

function PsychologicalFlexibilityDiagnostic() {
  const { code } = useParams(); // GUID from URL
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1=intro, 2=patterns

  const [assessmentData, setAssessmentData] = useState({
    code: code,
    scores: {
      flex1: 5, flex2: 5, flex3: 5, flex4: 5, flex5: 5,
      flex6: 5, flex7: 5, flex8: 5, flex9: 5, flex10: 5
    }
  });

  // 10 flexibility patterns (ACT-based inflexibility measures)
  const patterns = [
    { id: 1, title: 'Mind Wandering', question: 'How often does your mind drift away from what you\'re doing?' },
    { id: 2, title: 'Thought Entanglement', question: 'How often do you get caught up in unhelpful thoughts or worries?' },
    { id: 3, title: 'Emotional Avoidance', question: 'How often do you try to suppress or avoid difficult emotions?' },
    { id: 4, title: 'Self-Criticism', question: 'How often do you engage in harsh self-judgment or self-criticism?' },
    { id: 5, title: 'Values Disconnection', question: 'How often do you feel unclear about what truly matters to you?' },
    { id: 6, title: 'Inaction', question: 'How often do you avoid taking action because it feels too hard or uncomfortable?' },
    { id: 7, title: 'Rigidity', question: 'How often do you get stuck in rigid patterns of thinking or behaving?' },
    { id: 8, title: 'Experiential Avoidance', question: 'How often do you avoid uncomfortable situations or experiences?' },
    { id: 9, title: 'Cognitive Fusion', question: 'How often do you treat your thoughts as absolute truths rather than just thoughts?' },
    { id: 10, title: 'Behavioral Inflexibility', question: 'How often do you persist with unhelpful behaviors even when they\'re not working?' }
  ];

  // GUID validation on mount
  useEffect(() => {
    const checkAssessmentStatus = async () => {
      // Validate GUID format (UUIDv4)
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidPattern.test(code)) {
        setError('Invalid assessment code format');
        setLoading(false);
        return;
      }

      // Check if assessment is already completed
      try {
        const response = await fetch(`/api/get-psychological-flexibility-results?code=${code}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.results && data.results.completedAt) {
            navigate(`/diagnostics/psychological-flexibility/results/${code}`);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking assessment status:', error);
      }

      setLoading(false);
    };

    checkAssessmentStatus();
  }, [code, navigate]);

  // Handlers
  const handleScoreChange = (flexId, value) => {
    setAssessmentData(prev => ({
      ...prev,
      scores: { ...prev.scores, [flexId]: parseInt(value) }
    }));
  };

  // Get score color class
  const getScoreColorClass = (score) => {
    if (score >= 0 && score <= 2) return 'score-red';
    if (score >= 3 && score <= 4) return 'score-orange';
    if (score >= 5 && score <= 6) return 'score-amber';
    if (score >= 7 && score <= 8) return 'score-green';
    if (score >= 9 && score <= 10) return 'score-blue';
    return 'score-amber';
  };

  // Submit handler
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate using algorithms v7.0
      const calculations = calculatePsychologicalFlexibilityV2(assessmentData.scores);

      // Prepare payload matching API expected format
      const payload = {
        code: assessmentData.code,
        flexScores: assessmentData.scores, // API expects flexScores
        flexibility: { // API expects flexibility object
          totalScore: calculations.totalScore,
          averageScore: calculations.averageScore,
          psychologicalInflexibilityFactor: calculations.psychologicalInflexibilityFactor
        },
        wellbeing: { // API expects wellbeing object
          burdenLevel: calculations.burdenLevel,
          burdenDescription: calculations.burdenDescription,
          recommendations: calculations.recommendations
        }
      };

      // POST to API
      const response = await fetch('/api/save-psychological-flexibility-results', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to save results');
      }

      // Redirect to results
      navigate(`/diagnostics/psychological-flexibility/results/${assessmentData.code}`);

    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'Failed to submit assessment. Please try again.');
      setLoading(false);
    }
  };

  // Loading state
  if (loading && currentStep === 1) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '48px 16px' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
          <div className="card">
            <p style={{ textAlign: 'center', color: '#697A90' }}>Loading assessment...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && currentStep === 1) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '48px 16px' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
          <div className="card">
            <h1 style={{ color: '#ef4444' }}>Assessment Error</h1>
            <p style={{ color: '#697A90', marginTop: '16px' }}>{error}</p>
            <p style={{ color: '#697A90', marginTop: '8px', fontSize: '14px' }}>
              Please check your assessment link and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // STEP 1: Introduction
  // ============================================
  if (currentStep === 1) {
    return (
      <>
        <DiagnosticHeader />
        <div className="container">
        {/* Header Card */}
        <div className="card">
          <div className="badge">STEP 1 OF 3</div>
          <h1>Psychological Flexibility Diagnostic</h1>
          <p className="subtitle">
            Understand your psychological flexibility and how it impacts your wellbeing
          </p>

          <div className="info-box blue">
            <p><strong>What This Assessment Does:</strong></p>
            <p>This diagnostic measures patterns of psychological inflexibility based on Acceptance and Commitment Therapy (ACT) principles. It assesses how often you experience common inflexibility patterns that can impact your wellbeing and effectiveness.</p>
            <p style={{ marginTop: '12px' }}><strong>Important:</strong> This is a wellbeing-focused assessment with no transformation value calculations.</p>
          </div>
        </div>

        {/* Psychological Flexibility Explanation */}
        <div className="card">
          <h2>Understanding Psychological Flexibility</h2>
          <p>
            Psychological flexibility is the ability to stay present and engaged with your values even when difficult thoughts and emotions show up. It's not about eliminating discomfort—it's about responding effectively when life gets challenging.
          </p>

          <div className="info-box yellow">
            <p><strong>The 10 Inflexibility Patterns We Assess:</strong></p>
            <p>• <strong>Mind Wandering:</strong> Difficulty staying present with what you're doing</p>
            <p>• <strong>Thought Entanglement:</strong> Getting caught up in unhelpful thoughts</p>
            <p>• <strong>Emotional Avoidance:</strong> Trying to suppress or escape difficult emotions</p>
            <p>• <strong>Self-Criticism:</strong> Harsh self-judgment and negative self-talk</p>
            <p>• <strong>Values Disconnection:</strong> Unclear about what truly matters to you</p>
            <p>• <strong>Inaction:</strong> Avoiding action due to discomfort or fear</p>
            <p>• <strong>Rigidity:</strong> Getting stuck in inflexible thinking patterns</p>
            <p>• <strong>Experiential Avoidance:</strong> Avoiding uncomfortable situations</p>
            <p>• <strong>Cognitive Fusion:</strong> Treating thoughts as absolute truths</p>
            <p>• <strong>Behavioral Inflexibility:</strong> Persisting with unhelpful behaviors</p>
          </div>

          <div className="info-box orange">
            <p><strong>Evidence Base:</strong></p>
            <p>This assessment is grounded in Acceptance and Commitment Therapy (ACT), an evidence-based psychological intervention. ACT coaching has been shown to have an 8% effect size (η² = 0.08) in improving psychological flexibility and wellbeing outcomes.</p>
            <p style={{ marginTop: '8px' }}><em>Source: Skews, S. J., 2018, "Acceptance and commitment coaching: A meta-analysis"</em></p>
          </div>
        </div>

        {/* What to Expect */}
        <div className="card">
          <h2>What to Expect</h2>
          <p>This assessment takes approximately 5-10 minutes to complete and consists of three steps:</p>

          <div className="expect-grid">
            <div className="expect-item">
              <h3>Step 1: Introduction</h3>
              <p>Learn about psychological flexibility and the ACT framework behind this assessment</p>
            </div>

            <div className="expect-item">
              <h3>Step 2: Rate Patterns</h3>
              <p>Assess 10 inflexibility patterns based on how frequently you experience them (0 = never, 10 = constantly)</p>
            </div>

            <div className="expect-item">
              <h3>Step 3: Your Results</h3>
              <p>Receive your psychological flexibility profile with personalized recommendations for building flexibility skills</p>
            </div>
          </div>
        </div>

        {/* What You'll Learn */}
        <div className="card">
          <h2>What You'll Learn</h2>

          <div className="info-box blue">
            <p><strong>Your Flexibility Profile:</strong></p>
            <p>• Overall psychological inflexibility score</p>
            <p>• Burden level assessment (Low, Moderate, High, Critical)</p>
            <p>• Which specific patterns are most impacting your wellbeing</p>
          </div>

          <div className="info-box yellow">
            <p><strong>Personalized Recommendations:</strong></p>
            <p>• ACT-based interventions for your highest-impact patterns</p>
            <p>• Practical exercises and practices to build flexibility</p>
            <p>• Evidence-based strategies for wellbeing improvement</p>
          </div>

          <div className="info-box orange">
            <p><strong>Understanding Your Scores:</strong></p>
            <p>• How your patterns compare to research-based thresholds</p>
            <p>• What your scores mean for daily life and wellbeing</p>
            <p>• Priority areas for personal development</p>
          </div>
        </div>

        {/* The 10 Patterns Overview */}
        <div className="card">
          <h2>The 10 Inflexibility Patterns We Assess</h2>
          <p>These patterns are common experiences that can reduce psychological flexibility. You'll rate how often each one shows up in your life.</p>

          <div className="dependencies-grid">
            {patterns.map((pattern) => (
              <div key={pattern.id} className="dependency-item">
                <strong>{pattern.id}. {pattern.title}</strong>
                <p style={{ marginTop: '4px', fontSize: '13px', color: '#697A90' }}>
                  {pattern.question}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy */}
        <div className="card">
          <h2>Privacy & Confidentiality</h2>
          <p>Your responses are confidential and used only to generate your personalized diagnostic results. We do not share individual assessment data with third parties.</p>
          <p style={{ marginTop: '12px' }}>This assessment is designed for personal development purposes. If you're experiencing significant mental health concerns, please consult a qualified mental health professional.</p>
        </div>

        {/* Continue Button */}
        <div className="card" style={{ textAlign: 'center' }}>
          <button
            className="btn-primary"
            onClick={() => {
              setCurrentStep(2);
              window.scrollTo(0, 0);
            }}
            aria-label="Continue to Step 2: Pattern Assessment"
          >
            Begin Assessment
          </button>
        </div>

        {/* Design Note */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • Psychological Flexibility Diagnostic • v7.0.0 • Step 1 of 3</p>
        </div>
        </div>
      </>
    );
  }

  // ============================================
  // STEP 2: Pattern Assessment
  // ============================================
  if (currentStep === 2) {
    return (
      <>
        <DiagnosticHeader />
        <div className="container">
        {/* Header Card */}
        <div className="card">
          <div className="badge">STEP 2 OF 3</div>
          <h1>Inflexibility Pattern Assessment</h1>
          <p className="subtitle">
            For each pattern, rate how often you experience it from 0 (never) to 10 (constantly).
          </p>
        </div>

        {/* Scoring Guide */}
        <div className="scoring-guide">
          <div className="scoring-header">
            <div className="info-icon">i</div>
            <h2>Scoring Guide (0-10 Scale)</h2>
          </div>

          <div className="bands-container">
            <div className="band band-0-2">
              <div className="range">0-2</div>
              <div className="label">Flexible</div>
              <div className="description">(rarely experience this pattern)</div>
            </div>

            <div className="band band-3-4">
              <div className="range">3-4</div>
              <div className="label">Adaptive</div>
              <div className="description">(occasionally notice this pattern)</div>
            </div>

            <div className="band band-5-6">
              <div className="range">5-6</div>
              <div className="label">Moderate</div>
              <div className="description">(sometimes experience this pattern)</div>
            </div>

            <div className="band band-7-8">
              <div className="range">7-8</div>
              <div className="label">Frequent</div>
              <div className="description">(often notice this pattern)</div>
            </div>

            <div className="band band-9-10">
              <div className="range">9-10</div>
              <div className="label">High Inflexibility</div>
              <div className="description">(constantly experience this pattern)</div>
            </div>
          </div>

          <div className="usage-note">
            <strong>How to use:</strong> Rate how often each pattern shows up in your life.
            <span className="blue"> 0 = Never experiencing this pattern</span>,
            <span className="red"> 10 = Constantly experiencing this pattern</span>.
          </div>
        </div>

        {/* Pattern Items */}
        <div className="card">
          <h2>Rate All 10 Inflexibility Patterns</h2>

          {patterns.map((pattern) => {
            const flexId = `flex${pattern.id}`;
            const score = assessmentData.scores[flexId];
            const colorClass = getScoreColorClass(score);

            return (
              <div key={pattern.id} className="pattern-item">
                <div className="inflexibility-header">
                  <div className="inflexibility-title">
                    <h3>{pattern.id}. {pattern.title}</h3>
                    <p className="pattern-question">{pattern.question}</p>
                  </div>
                  <div className="inflexibility-score">
                    <span className={`value ${colorClass}`}>{score}</span>
                    <span className="max">/10</span>
                  </div>
                </div>
                <div className="slider-container">
                  <input
                    type="range"
                    id={`slider-${flexId}`}
                    min="0"
                    max="10"
                    value={score}
                    onChange={(e) => handleScoreChange(flexId, e.target.value)}
                    aria-label={`${pattern.title}: ${pattern.question} Rate from 0 (flexible, rarely experience) to 10 (high inflexibility, constantly experience)`}
                    aria-valuemin="0"
                    aria-valuemax="10"
                    aria-valuenow={score}
                    aria-valuetext={`${score} out of 10: ${
                      score >= 0 && score <= 2 ? 'Flexible' :
                      score >= 3 && score <= 4 ? 'Adaptive' :
                      score >= 5 && score <= 6 ? 'Moderate' :
                      score >= 7 && score <= 8 ? 'Frequent inflexibility' :
                      'High inflexibility'
                    }`}
                  />
                  <div className="slider-labels" role="presentation" aria-hidden="true">
                    <span className="label-blue">0-2<br/>Flexible</span>
                    <span className="label-green">3-4<br/>Adaptive</span>
                    <span className="label-amber">5-6<br/>Moderate</span>
                    <span className="label-orange">7-8<br/>Frequent</span>
                    <span className="label-red">9-10<br/>High Inflexibility</span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', gap: '16px', marginTop: '32px' }}>
            <button
              className="btn-back"
              onClick={() => {
                setCurrentStep(1);
                window.scrollTo(0, 0);
              }}
              aria-label="Go back to Step 1: Introduction"
            >
              ← Back to Introduction
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
              style={{ flex: 1 }}
              aria-label={loading ? 'Submitting assessment data' : 'Generate results and proceed to Step 3'}
              aria-disabled={loading}
            >
              {loading ? 'Generating Results...' : 'Generate Results →'}
            </button>
          </div>

          {error && (
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px' }}>
              <p style={{ color: '#dc2626', fontSize: '14px' }}>
                <strong>Error:</strong> {error}
              </p>
            </div>
          )}
        </div>

        {/* Design Note */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • Psychological Flexibility Diagnostic • v7.0.0 • Step 2 of 3</p>
        </div>
        </div>
      </>
    );
  }

  return null;
}

export default PsychologicalFlexibilityDiagnostic;
